import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ProductForm } from './ProductFormShared';

const api = axios.create({ baseURL: '/api' });
api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

function mapOrigin(text) {
  if (!text) return '기타';
  if (text === '상세설명 참고') return '기타';
  if (text.includes('국산') || text.includes('국내산')) return '국산';
  if (/산(\(|$)/.test(text)) return '수입산';
  return '기타';
}

function calcTotalCapacity(name) {
  if (!name) return '';
  const weightMatch = name.match(/(\d+(?:\.\d+)?)\s*(g|kg|ml|l)\b/i);
  const qtyMatch = name.match(/(\d+)\s*개(?!입)/);
  if (weightMatch && qtyMatch) {
    const weight = parseFloat(weightMatch[1]);
    const qty = parseInt(qtyMatch[1], 10);
    return String(weight * qty);
  }
  return '';
}

const NOTICE_TYPE_BY_LARGE_CATEGORY = {
  '식품': 'GENERAL_FOOD',
  '생활/건강': 'KITCHEN_UTENSILS',
  '화장품/미용': 'COSMETIC',
};

// ===== 지마켓 전용 시작 (나중에 이 기능만 제거할 경우, 아래부터 "지마켓 전용 끝" 표시까지 삭제) =====
function isGmarketUrl(url) {
  return /gmarket\.co\.kr/i.test(url || '');
}

async function buildGmarketProduct(data, api) {
  const product = {
    stock: 1000,
    expected_price: data['가격'] || '',
    brand: data['브랜드'] || '',
    manufacturer: data['제조사'] || '',
    origin: mapOrigin(data['원산지']),
    name: data['상품명'] || '',
    total_capacity_value: calcTotalCapacity(data['상품명']),
    category_id: '',
    notice_type: '',
    large_category: '',
    middle_category: '',
    small_category: '',
    detail_category: '',
  };

  let categoryMatched = false;
  if (data['최하위카테고리명']) {
    try {
      const r = await api.get(`/products/category-by-name/${encodeURIComponent(data['최하위카테고리명'])}`);
      if (r.data.found) {
        const cat = r.data.category;
        product.category_id = cat.id;
        product.large_category = cat.large_category;
        product.middle_category = cat.middle_category;
        product.small_category = cat.small_category;
        product.detail_category = cat.detail_category;
        categoryMatched = true;
        if (NOTICE_TYPE_BY_LARGE_CATEGORY[cat.large_category]) {
          product.notice_type = NOTICE_TYPE_BY_LARGE_CATEGORY[cat.large_category];
        }
      }
    } catch (e) { /* 카테고리 조회 실패 시 무시 */ }
  }

  return { product, categoryMatched, gmarketCategoryPath: (data['카테고리경로'] || []).join(' > ') };
}
// ===== 지마켓 전용 끝 =====

// ===== 풀무원 공식몰 전용 시작 (나중에 이 기능만 제거할 경우, 아래부터 "풀무원 전용 끝" 표시까지 삭제) =====
function isPulmuoneUrl(url) {
  return /shop\.pulmuone\.co\.kr/i.test(url || '');
}

function buildPulmuoneProduct(data) {
  // [기능 추가 - 2026-09-11] 풀무원은 카테고리 매칭 정보를 제공하지 않아, 카테고리는
  // 사용자가 직접 선택해야 한다. 대신 상품명/가격/보관방법/원산지는 정확히 채운다.
  const detailHtml = (data['상세이미지목록'] || [])
    .map(url => `<img src="${url}" style="max-width:100%;display:block;margin:8px auto;" />`)
    .join('');
  const product = {
    stock: 1000,
    expected_price: data['가격'] || '',
    brand: '',
    manufacturer: '',
    origin: mapOrigin(data['원산지']),
    name: data['상품명'] || '',
    storage_method: data['보관방법'] || '',
    total_capacity_value: calcTotalCapacity(data['상품명']),
    description: detailHtml,
    category_id: '',
    notice_type: '',
    large_category: '',
    middle_category: '',
    small_category: '',
    detail_category: '',
  };
  return { product };
}
// ===== 풀무원 공식몰 전용 끝 =====

function ManageFavoritesModal({ favorites, onClose, onDelete, onEdit, onAddNew }) {
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '400px' }}>
        <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px' }}>대표스토어 관리</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
          {favorites.length === 0 && (
            <div style={{ fontSize: '12px', color: '#aaa', textAlign: 'center', padding: '20px 0' }}>등록된 대표스토어가 없습니다.</div>
          )}
          {favorites.map(f => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px', borderRadius: '8px', background: '#fafafa' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '12px', fontWeight: '500', color: '#1a1a1a' }}>{f.name}</div>
                <div style={{ fontSize: '10px', color: '#aaa', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.url}</div>
              </div>
              <button onClick={() => onEdit(f)} style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #B5D4F4', background: '#fff', color: '#185FA5', cursor: 'pointer', flexShrink: 0 }}>수정</button>
              <button onClick={() => onDelete(f.id)} style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #ffcdd2', background: '#fff', color: '#e53935', cursor: 'pointer', flexShrink: 0 }}>삭제</button>
            </div>
          ))}
        </div>
        {favorites.length < 5 && (
          <button onClick={onAddNew} style={{ width: '100%', fontSize: '12px', padding: '10px', borderRadius: '8px', border: '0.5px dashed #bbb', background: '#fafafa', color: '#666', cursor: 'pointer', marginBottom: '10px' }}>
            + 새 대표스토어 추가
          </button>
        )}
        <button onClick={onClose} style={{ width: '100%', fontSize: '13px', padding: '10px', borderRadius: '8px', border: 'none', background: '#1D9E75', color: '#fff', cursor: 'pointer', fontWeight: '500' }}>닫기</button>
      </div>
    </div>
  );
}

function AddFavoriteModal({ onClose, onSaved, editTarget }) {
  const [name, setName] = useState(editTarget?.name || '');
  const [url, setUrl] = useState(editTarget?.url || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim() || !url.trim()) {
      alert('스토어 이름과 URL을 모두 입력해주세요');
      return;
    }
    setSaving(true);
    try {
      if (editTarget) {
        await api.patch(`/products/matching-store-favorites/${editTarget.id}`, { name: name.trim(), url: url.trim() });
      } else {
        await api.post('/products/matching-store-favorites', { name: name.trim(), url: url.trim() });
      }
      onSaved();
      onClose();
    } catch (e) {
      alert(e.response?.data?.message || '저장 실패');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '360px' }}>
        <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '16px' }}>{editTarget ? '대표스토어 수정' : '대표스토어 추가'}</div>
        <div style={{ marginBottom: '10px' }}>
          <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>스토어 이름</div>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="예: 하우스마트"
            style={{ fontSize: '13px', padding: '7px 10px', borderRadius: '8px', border: '0.5px solid #ddd', width: '100%', boxSizing: 'border-box' }} />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>URL</div>
          <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://smartstore.naver.com/..."
            style={{ fontSize: '13px', padding: '7px 10px', borderRadius: '8px', border: '0.5px solid #ddd', width: '100%', boxSizing: 'border-box' }} />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={handleSave} disabled={saving} style={{ flex: 1, fontSize: '13px', padding: '10px', borderRadius: '8px', border: 'none', background: saving ? '#ccc' : '#1D9E75', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: '500' }}>
            {saving ? '저장 중...' : '저장'}
          </button>
          <button onClick={onClose} style={{ flex: 1, fontSize: '13px', padding: '10px', borderRadius: '8px', border: '0.5px solid #ddd', background: '#fff', color: '#666', cursor: 'pointer' }}>
            취소
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProductMatchRegister() {
  const navigate = useNavigate();
  const [urlInput, setUrlInput] = useState('');
  const [loadedUrl, setLoadedUrl] = useState('');
  const [extensionReady, setExtensionReady] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractedProduct, setExtractedProduct] = useState(null);
  const [formKey, setFormKey] = useState(0);
  const [extractMsg, setExtractMsg] = useState('');
  const [favorites, setFavorites] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingFavorite, setEditingFavorite] = useState(null);
  const [showManageModal, setShowManageModal] = useState(false);
  const [fetchImage, setFetchImage] = useState(false);
  const [gmarketCategoryInfo, setGmarketCategoryInfo] = useState(null);
  const [referenceImageUrl, setReferenceImageUrl] = useState(null);
  const [downloadingImage, setDownloadingImage] = useState(false);

  const fetchFavorites = useCallback(async () => {
    try {
      const r = await api.get('/products/matching-store-favorites');
      setFavorites(r.data);
    } catch (e) { /* 무시 */ }
  }, []);

  useEffect(() => { fetchFavorites(); }, [fetchFavorites]);

  const loadFavorite = (url) => {
    setUrlInput(url);
    setLoadedUrl(url);
    setExtractMsg('');
    setExtractedProduct(null);
  };

  const deleteFavorite = async (id) => {
    if (!window.confirm('이 즐겨찾기를 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/products/matching-store-favorites/${id}`);
      fetchFavorites();
    } catch (e) {
      alert('삭제 실패');
    }
  };

  const buildExtractedProduct = useCallback(async (info) => {
    const product = {
      stock: 1000,
      expected_price: (info['가격'] || 0) + (info['배송비'] || 0) || '',
      brand: info['브랜드'] && info['브랜드'] !== '상세설명 참고' ? info['브랜드'] : '',
      manufacturer: info['제조사'] || '',
      origin: mapOrigin(info['원산지']),
      name: info['상품명'] || '',
      total_capacity_value: calcTotalCapacity(info['상품명']),
      category_id: '',
      notice_type: '',
      large_category: '',
      middle_category: '',
      small_category: '',
      detail_category: '',
    };

    if (info['카테고리ID']) {
      try {
        const r = await api.get(`/products/category-by-naver-id/${info['카테고리ID']}`);
        if (r.data.found) {
          const cat = r.data.category;
          product.category_id = cat.id;
          product.large_category = cat.large_category;
          product.middle_category = cat.middle_category;
          product.small_category = cat.small_category;
          product.detail_category = cat.detail_category;
          if (NOTICE_TYPE_BY_LARGE_CATEGORY[cat.large_category]) {
            product.notice_type = NOTICE_TYPE_BY_LARGE_CATEGORY[cat.large_category];
          }
        }
      } catch (e) { /* 카테고리 조회 실패 시 무시하고 넘어감 */ }
    }

    return product;
  }, []);

  useEffect(() => {
    const handler = async (event) => {
      if (event.source !== window) return;
      if (event.data?.type === 'JINHA_EXTENSION_READY') {
        setExtensionReady(true);
      }
      if (event.data?.type === 'JINHA_EXTRACT_RESULT') {
        const res = event.data.payload;
        if (!res || !res.success) {
          setExtracting(false);
          setExtractMsg('가져오기 실패: ' + (res?.error || '알 수 없는 오류'));
          return;
        }
        const product = await buildExtractedProduct(res.data || {});
        setExtractedProduct(product);
        setFormKey(k => k + 1);
        setExtracting(false);
        setExtractMsg('정보를 가져왔습니다. 내용을 확인 후 저장해주세요.');

        setReferenceImageUrl(null);
        if (fetchImage && res.data?.['대표이미지']) {
          setDownloadingImage(true);
          try {
            const imgRes = await api.post('/products/download-reference-image', { imageUrl: res.data['대표이미지'] });
            setReferenceImageUrl(imgRes.data.url);
          } catch (e) {
            console.error('참고 이미지 다운로드 실패', e);
          } finally {
            setDownloadingImage(false);
          }
        }
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [buildExtractedProduct, fetchImage]);

  const handleLoad = () => {
    let url = urlInput.trim();
    if (!url) return;
    if (!/^https?:\/\//.test(url)) url = 'https://' + url;
    setLoadedUrl(url);
    setExtractMsg('');
    setExtractedProduct(null);
  };

  const handleExtract = useCallback(async () => {
    if (!loadedUrl) return;
    setExtracting(true);
    setExtractMsg('');

    // ===== 지마켓 전용 분기 시작 =====
    if (isGmarketUrl(loadedUrl)) {
      try {
        const r = await api.get('/products/scrape-gmarket', { params: { url: loadedUrl } });
        if (r.data.success) {
          const { product, categoryMatched, gmarketCategoryPath } = await buildGmarketProduct(r.data.data, api);
          setExtractedProduct(product);
          setFormKey(k => k + 1);
          setGmarketCategoryInfo({ path: gmarketCategoryPath, matched: categoryMatched });
          setExtractMsg('정보를 가져왔습니다. 내용을 확인 후 저장해주세요.');

          setReferenceImageUrl(null);
          if (fetchImage && r.data.data?.['대표이미지']) {
            setDownloadingImage(true);
            try {
              const imgRes = await api.post('/products/download-reference-image', { imageUrl: r.data.data['대표이미지'] });
              setReferenceImageUrl(imgRes.data.url);
            } catch (e2) {
              console.error('참고 이미지 다운로드 실패', e2);
            } finally {
              setDownloadingImage(false);
            }
          }
        } else {
          setExtractMsg('가져오기 실패: ' + (r.data.message || '알 수 없는 오류'));
        }
      } catch (e) {
        setExtractMsg('가져오기 실패: ' + (e.response?.data?.message || e.message));
      } finally {
        setExtracting(false);
      }
      return;
    }
    // ===== 지마켓 전용 분기 끝 =====

    // ===== 풀무원 공식몰 전용 분기 시작 =====
    if (isPulmuoneUrl(loadedUrl)) {
      try {
        const r = await api.get('/products/scrape-pulmuone', { params: { url: loadedUrl } });
        if (r.data.success) {
          const { product } = buildPulmuoneProduct(r.data.data);
          setExtractedProduct(product);
          setFormKey(k => k + 1);
          setExtractMsg('정보를 가져왔습니다. 카테고리는 직접 선택 후 저장해주세요.');
        } else {
          setExtractMsg('가져오기 실패: ' + (r.data.message || '알 수 없는 오류'));
        }
      } catch (e) {
        setExtractMsg('가져오기 실패: ' + (e.response?.data?.message || e.message));
      } finally {
        setExtracting(false);
      }
      return;
    }
    // ===== 풀무원 공식몰 전용 분기 끝 =====

    window.postMessage({ type: 'JINHA_REQUEST_EXTRACT' }, '*');
  }, [loadedUrl, fetchImage]);

  const handleSave = () => {
    navigate('/products');
  };

  const handleCancel = () => {
    navigate('/products');
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: '#f5f5f5' }}>
      <div style={{ padding: '10px 16px', background: '#fff', borderBottom: '0.5px solid #e0e0e0', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'nowrap' }}>
        <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a', marginRight: '6px', flexShrink: 0 }}>매칭스토어 등록</span>
        <input
          value={urlInput}
          onChange={e => setUrlInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleLoad()}
          placeholder="상품 URL 입력"
          style={{ flex: '0 1 340px', minWidth: '160px', fontSize: '12px', padding: '6px 10px', borderRadius: '8px', border: '0.5px solid #ddd' }}
        />
        <button onClick={handleLoad} style={{ fontSize: '12px', padding: '6px 12px', borderRadius: '8px', border: 'none', background: '#1D9E75', color: '#fff', cursor: 'pointer', fontWeight: '500', flexShrink: 0 }}>
          불러오기
        </button>
        {extensionReady ? (
          <button onClick={handleExtract} disabled={!loadedUrl || extracting}
            style={{ fontSize: '12px', padding: '6px 12px', borderRadius: '8px', border: '0.5px solid #378ADD', background: (!loadedUrl || extracting) ? '#f0f0f0' : '#E6F1FB', color: (!loadedUrl || extracting) ? '#aaa' : '#378ADD', cursor: (!loadedUrl || extracting) ? 'not-allowed' : 'pointer', fontWeight: '500', flexShrink: 0, whiteSpace: 'nowrap' }}>
            {extracting ? '가져오는 중...' : '자동 가져오기'}
          </button>
        ) : (
          <span style={{ fontSize: '10px', color: '#aaa', flexShrink: 0, whiteSpace: 'nowrap' }}>확장프로그램 미설치</span>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#666', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={fetchImage} onChange={e => setFetchImage(e.target.checked)} />
          대표이미지도 함께 가져오기
        </label>

        <span style={{ flex: 1 }} />

        {favorites.map(f => (
          <button key={f.id} onClick={() => loadFavorite(f.url)}
            style={{ fontSize: '11px', padding: '5px 12px', borderRadius: '6px', border: '0.5px solid #B5D4F4', background: '#E6F1FB', color: '#185FA5', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: '500', flexShrink: 0 }}>
            {f.name}
          </button>
        ))}
        <button onClick={() => setShowManageModal(true)}
          style={{ fontSize: '11px', padding: '5px 12px', borderRadius: '6px', border: '0.5px solid #ccc', background: '#f0f0f0', color: '#555', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap', fontWeight: '500' }}>
          대표스토어 관리
        </button>
      </div>

      {(extractMsg || downloadingImage || referenceImageUrl) && (
        <div style={{ padding: '6px 16px', background: '#fafafa', borderBottom: '0.5px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: '10px' }}>
          {extractMsg && <span style={{ fontSize: '12px', color: extractMsg.startsWith('가져오기 실패') ? '#E24B4A' : '#1D9E75' }}>{extractMsg}</span>}
          {downloadingImage && <span style={{ fontSize: '12px', color: '#aaa' }}>참고 이미지 다운로드 중...</span>}
          {referenceImageUrl && (
            <a href={`https://admin.jinhalife.co.kr${referenceImageUrl}`} download target="_blank" rel="noreferrer"
              style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #1D9E75', color: '#1D9E75', textDecoration: 'none', fontWeight: '500' }}>
              참고 이미지 다운로드
            </a>
          )}
        </div>
      )}


      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <div style={{ width: '50%', borderRight: '1px solid #e0e0e0', background: '#fff' }}>
          {loadedUrl ? (
            <iframe
              src={loadedUrl}
              title="상품 참고 페이지"
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#aaa', fontSize: '13px' }}>
              왼쪽 상단에 URL을 입력하고 불러오기를 눌러주세요.<br/>(지마켓, 옥션 등 일부 사이트는 표시가 차단될 수 있습니다)
            </div>
          )}
        </div>
        <div style={{ width: '50%', overflowY: 'auto', padding: '16px' }}>
          {gmarketCategoryInfo && (
            <div style={{ textAlign: 'right', marginBottom: '10px' }}>
              <span style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '6px', background: gmarketCategoryInfo.matched ? '#E1F5EE' : '#FAEEDA', color: gmarketCategoryInfo.matched ? '#085041' : '#854F0B' }}>
                지마켓 카테고리: {gmarketCategoryInfo.path} — {gmarketCategoryInfo.matched ? '일치, 카테고리 자동 선택됨' : '일치하는 카테고리 없음, 직접 선택해주세요'}
              </span>
            </div>
          )}
          <ProductForm key={formKey} product={extractedProduct} onSave={handleSave} onCancel={handleCancel} titleOverride={extractedProduct ? '매칭 정보로 등록' : '새 상품 등록'} />
        </div>
      </div>

      {showManageModal && !showAddModal && !editingFavorite && (
        <ManageFavoritesModal
          favorites={favorites}
          onClose={() => setShowManageModal(false)}
          onDelete={(id) => deleteFavorite(id)}
          onEdit={(f) => setEditingFavorite(f)}
          onAddNew={() => setShowAddModal(true)}
        />
      )}
      {showAddModal && (
        <AddFavoriteModal onClose={() => setShowAddModal(false)} onSaved={fetchFavorites} />
      )}
      {editingFavorite && (
        <AddFavoriteModal editTarget={editingFavorite} onClose={() => setEditingFavorite(null)} onSaved={fetchFavorites} />
      )}
    </div>
  );
}
